import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, RotateCw } from "lucide-react";
import { useRadio } from "@/lib/RadioContext";
import { fmt } from "@/lib/radioSchedule";

const SPEEDS = [1, 1.25, 1.5, 0.85];

export function EssaiAudio({ src, seconds, label }) {
  const ref = useRef(null);
  const radio = useRadio();
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(seconds || 0);
  const [speed, setSpeed] = useState(0);

  useEffect(() => { setPlaying(false); setT(0); }, [src]);
  const toggle = () => {
    const a = ref.current;
    if (a.paused) { if (radio.playing) radio.pause(); a.play(); } else a.pause();
  };
  const skip = (d) => { ref.current.currentTime = Math.max(0, Math.min(dur, ref.current.currentTime + d)); };
  const cycle = () => { const n = (speed + 1) % SPEEDS.length; setSpeed(n); ref.current.playbackRate = SPEEDS[n]; };
  const seek = (e) => { ref.current.currentTime = (Number(e.target.value) / 1000) * dur; };
  const ctl = "h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors";

  return (
    <div className="rounded-3xl border border-primary/25 bg-card/50 backdrop-blur-xl p-5 sm:p-6" data-testid="essai-audio">
      <audio ref={ref} src={src} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setT(e.target.currentTime)} onLoadedMetadata={(e) => setDur(e.target.duration)} onEnded={() => setPlaying(false)} />
      <p className="text-[0.7rem] uppercase tracking-[0.24em] text-primary mb-4">{label}</p>
      <div className="flex items-center gap-3">
        <button onClick={toggle} data-testid="essai-audio-play-btn" aria-label="play"
          className="h-14 w-14 shrink-0 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:scale-105 transition-transform">
          {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
        </button>
        <button onClick={() => skip(-15)} className={ctl} data-testid="essai-audio-back-btn" aria-label="-15s"><RotateCcw size={16} /></button>
        <button onClick={() => skip(15)} className={ctl} data-testid="essai-audio-fwd-btn" aria-label="+15s"><RotateCw size={16} /></button>
        <button onClick={cycle} className={`${ctl} text-xs font-semibold w-12`} data-testid="essai-audio-speed-btn">{SPEEDS[speed]}×</button>
      </div>
      <input type="range" min="0" max="1000" value={dur ? (t / dur) * 1000 : 0} onChange={seek} data-testid="essai-audio-seek"
        className="mt-5 w-full accent-[hsl(var(--primary))]" />
      <div className="flex justify-between text-xs text-foreground/50 tabular-nums" data-testid="essai-audio-time">
        <span>{fmt(t)}</span><span>{fmt(dur)}</span>
      </div>
    </div>
  );
}
